import React from 'react';
import { t } from '@citrite/translate';
import {
	CheckmarkInCircleSolidIcon,
	CircleIcon,
	ExclamationInDiamondSolidIcon,
	IconBadge,
	IInCircleSolidIcon,
	layout,
	NotificationBadgeHalfIcon,
	NotificationBadgeIcon,
	palette,
	SettingsWrenchSolidIcon,
	Tooltip,
	typography,
	UsersSharedIcon,
} from '@citrite/web-ui-component';
import { SkeletonLoaderShapes } from '@citrite/workspace-ui-platform-react';
import styled from '@emotion/styled';
import moment from 'moment';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { getTimestamp } from 'App/Activity/Utility';
import { getResourceSubText } from 'App/Screen/util';
import { isSharedResource } from 'App/Tile/utils';
import { detectLanguage } from 'javascript/sf/detectLanguage';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

const { LoadingBar } = SkeletonLoaderShapes;
interface Props {
	resource: Resource;
	powerState: PowerState;
	lastUpdatedTime: string;
	canShowTroubleshootStatus?: boolean;
}

const Wrapper = styled.div`
	display: block;
`;

const LoadingBarWrapper = styled.div`
	height: ${layout.space22};
	display: flex;
	align-items: center;
`;

const CustomIconBadge = styled(IconBadge)`
	height: ${layout.space22};
	font-size: ${typography.sizes.xsml2};
	line-height: normal;
	padding: ${layout.tinySpace} ${layout.space6};
	display: inline-flex;
	align-items: center;
	text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
`;

const iconSize = 10;
const textColor = palette.white;

interface BadgeWithTooltipProps {
	icon: React.ReactNode;
	backgroundColor: string;
	text: string;
	tooltipText: string;
	isDisabled?: boolean;
	onMouseEnter?: () => void;
}

const BadgeWithTooltip: React.FC<BadgeWithTooltipProps> = ({
	icon,
	backgroundColor,
	text,
	tooltipText,
	isDisabled = false,
	onMouseEnter,
}) => (
	<Tooltip
		hoverTrigger={
			<Wrapper onMouseEnter={onMouseEnter}>
				<CustomIconBadge
					icon={icon}
					backgroundColor={backgroundColor}
					textColor={textColor}
					children={text}
				/>
			</Wrapper>
		}
		isDisabled={isDisabled}
	>
		{tooltipText}
	</Tooltip>
);

export const PowerStateBadge: React.FC<Props> = ({
	resource,
	powerState,
	lastUpdatedTime,
	canShowTroubleshootStatus = false,
}) => {
	const [powerStateTimeStamp, setpowerStateTimeStamp] = React.useState<string>('');
	const isSharedDesktop = isSharedResource(resource);
	const { maintenanceMode, summaryState } = useDesktopDetails(resource);
	const lastUpdatedTimestamp = getTimestamp(lastUpdatedTime);

	const getPowerStatusAge = () => {
		if (powerState === PowerState.UNMANAGED) {
			return t('Workspace:activity_manager.tooltip_state_unmanaged');
		}

		const time = moment(lastUpdatedTimestamp).locale(detectLanguage()).format('LT');
		return t('Workspace:activity_manager.tooltip_state_last_updated', { time });
	};

	const handlePowerStatusAge = () => {
		const machineStatusAge = getPowerStatusAge();
		setpowerStateTimeStamp(machineStatusAge);
	};

	if (canShowTroubleshootStatus) {
		return (
			<BadgeWithTooltip
				icon={<ExclamationInDiamondSolidIcon size={iconSize} color={palette.white} />}
				backgroundColor={palette.orange800}
				text={t('Workspace:inspect_connection.troubleshoot')}
				tooltipText={t('Workspace:inspect_connection.troubleshoot_tooltip')}
			/>
		);
	}

	if (
		isSharedDesktop ||
		powerState === PowerState.NOT_SUPPORTED ||
		powerState === PowerState.UNKNOWN
	) {
		const Icon = isSharedDesktop ? UsersSharedIcon : IInCircleSolidIcon;
		const text = isSharedDesktop
			? t('Workspace:ftu_ui.shared_desktop')
			: getResourceSubText(resource.type);

		return (
			<BadgeWithTooltip
				icon={<Icon size={iconSize} color={palette.white} />}
				backgroundColor={palette.blue900}
				text={text}
				tooltipText={t('Workspace:activity_manager.tooltip_badge_pooled_desktop')}
			/>
		);
	}

	const powerStateConfig: Record<
		string,
		{ icon: React.ComponentType<any>; backgroundColor: string; text: string }
	> = {
		[PowerState.OFF]: {
			icon: CircleIcon,
			backgroundColor: palette.grey600,
			text: t('Workspace:activity_manager.powered_off'),
		},
		[PowerState.ON]: {
			icon: CircleIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.powered_on'),
		},
		[PowerState.READY]: {
			icon: CheckmarkInCircleSolidIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.ready'),
		},
		[PowerState.UNAVAILABLE]: {
			icon: ExclamationInDiamondSolidIcon,
			backgroundColor: palette.grey700,
			text: t('Workspace:activity_manager.unavailable'),
		},
		[PowerState.SUSPENDED]: {
			icon: NotificationBadgeHalfIcon,
			backgroundColor: palette.yellow950,
			text: t('Workspace:activity_manager.powerstate_in_Hibernation'),
		},
		[PowerState.SUSPENDING]: {
			icon: CircleIcon,
			backgroundColor: palette.teal700,
			text: t('Workspace:activity_manager.hibernating'),
		},
		[PowerState.TURNING_OFF]: {
			icon: CircleIcon,
			backgroundColor: palette.teal700,
			text: t('Workspace:activity_manager.shutting_down'),
		},
		[PowerState.TURNING_ON]: {
			icon: CircleIcon,
			backgroundColor: palette.teal700,
			text: t('Workspace:launch_status.powering-on'),
		},
		[PowerState.STARTING]: {
			icon: CircleIcon,
			backgroundColor: palette.teal700,
			text: t('Workspace:activity_manager.starting'),
		},
		UnderMaintenance: {
			icon: SettingsWrenchSolidIcon,
			backgroundColor: palette.grey700,
			text: t('Workspace:activity_manager.under_maintenance'),
		},
		Active: {
			icon: NotificationBadgeIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.active'),
		},
	};

	// Loading state special case
	if (powerState === PowerState.LOADING) {
		return (
			<Wrapper>
				<LoadingBarWrapper>
					<LoadingBar
						width="80px"
						topMargin={layout.tinySpace}
						bottomMargin={layout.tinySpace}
						leftMargin={layout.space2}
					/>
				</LoadingBarWrapper>
			</Wrapper>
		);
	}

	// Determine which badge to show based on priority
	let stateKey: string;
	if (maintenanceMode) {
		stateKey = 'UnderMaintenance';
	} else if (summaryState === 'InUse') {
		stateKey = 'Active';
	} else {
		stateKey = powerState;
	}

	const config = powerStateConfig[stateKey] || powerStateConfig[PowerState.UNAVAILABLE];
	const IconComponent = config.icon;

	return (
		<BadgeWithTooltip
			icon={<IconComponent size={iconSize} color={palette.white} />}
			backgroundColor={config.backgroundColor}
			text={config.text}
			tooltipText={powerStateTimeStamp}
			onMouseEnter={handlePowerStatusAge}
		/>
	);
};
